import React, { useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';
import { BackgroundBeams } from './BackgroundBeams';

import './Hero.css';

const Hero = () => {
    const containerRef = useRef(null);

    // Mouse parallax tracking
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    useEffect(() => {
        const handleMouseMove = (e) => {
            const { clientX, clientY } = e;
            const { innerWidth, innerHeight } = window;
            const x = (clientX / innerWidth) - 0.5;
            const y = (clientY / innerHeight) - 0.5;
            mouseX.set(x * 30);
            mouseY.set(y * 30);
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [mouseX, mouseY]);

    const springX = useSpring(mouseX, { stiffness: 40, damping: 25 });
    const springY = useSpring(mouseY, { stiffness: 40, damping: 25 });

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end start"]
    });

    const scrollY = useTransform(scrollYProgress, [0, 1], [0, -200]);
    const scrollOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

    // Reverse motion transforms for parallax contrast
    const springXReverse = useTransform(springX, v => -v);
    const springYReverse = useTransform(springY, v => -v);

    return (
        <section className="hero-obsidian" ref={containerRef}>
            {/* Cinematic Mesh Background */}
            <div className="hero-mesh-bg">
                <motion.div
                    className="mesh-gradient mesh-1"
                    style={{ x: springX, y: springY }}
                />
                <motion.div
                    className="mesh-gradient mesh-2"
                    style={{ x: springXReverse, y: springYReverse }}
                />
                <motion.div
                    className="mesh-gradient mesh-3"
                    style={{ x: springX, y: springYReverse }}
                />
                <motion.div
                    className="mesh-gradient mesh-4"
                    style={{ x: springXReverse, y: springY }}
                />
            </div>

            <div className="obsidian-grain" />
            <div className="obsidian-aura" />
            <BackgroundBeams />

            {/* Central Elite Content */}
            <motion.div
                className="obsidian-content"
                style={{ y: scrollY, opacity: scrollOpacity }}
            >
                <div className="obsidian-info">
                    <span className="info-line"></span>
                    <span className="info-text">EST. 2025 // DEPLOY-X     .</span>
                </div>

                <h1 className="obsidian-title">
                    <span className="title-row row-1">ENGINEERING</span>
                    <span className="title-row row-2">
                        THE <span className="title-highlight">FUTURE</span>
                    </span>
                    <span className="title-row row-3">WITH DIGITAL EXCELLENCE</span>
                </h1>

                <p className="obsidian-description">
                    Elite software engineering and AI infrastructure. We harness
                    the power of intelligent logic to build world-class legacies.
                </p>

                <div className="obsidian-actions">
                    <a href="/contact" className="action-button primary">
                        <span className="btn-label">START PROJECT</span>
                    </a>
                    <a href="/services" className="action-button secondary">
                        <span className="btn-label">OUR CAPABILITIES</span>
                    </a>
                </div>
            </motion.div>

            {/* Corner Metrics */}
            <div className="obsidian-metrics">
                <div className="metric">
                    <span className="m-val">99%</span>
                    <span className="m-lab">UPTIME</span>
                </div>
                <div className="metric">
                    <span className="m-val">50+</span>
                    <span className="m-lab">DEPLOYS</span>
                </div>
            </div>

            <div className="obsidian-scroll">
                <div className="scroll-indicator" />
                <span>SCROLL TO EXPLORE</span>
            </div>
        </section>
    );
};

export default Hero;
